import {
  DndContext,
  DragEndEvent,
  MouseSensor,
  TouchSensor,
  UniqueIdentifier,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  restrictToParentElement,
  restrictToVerticalAxis,
} from "@dnd-kit/modifiers";
import {
  SortableContext,
  arrayMove,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import Image from "next/image";
import { usePathname, useRouter } from "next/navigation";
import { useState } from "react";
import { isMobile } from "react-device-detect";
import { toast } from "react-hot-toast";
import { Balancer } from "react-wrap-balancer";

import { ErrorView } from "@/components/error-view";
import { useWatchlistContext } from "@/context/use-context/watchlist-provider/client";
import { indexOrientedMap } from "@/data/constants/oriented";
import { useActiveWatchlist } from "@/features/watchlist/services/get-active-watchlist";
import { useSymbolsScore } from "@/features/watchlist/services/get-symbols-score";
import { useUpdateActiveWatchlist } from "@/features/watchlist/services/update-active-watchlist";
import { useLang } from "@/hooks/use-lang";
import emptyImage from "@/public/global/empty.svg";

import { SymbolItem } from "./SymbolItem";

type SymbolListProps = {
  orientedIndex: 0 | 1 | 2 | 3 | 4;
};

export const SymbolList = ({ orientedIndex = 0 }: SymbolListProps) => {
  const { t, lang } = useLang();
  const pathname = usePathname();
  const router = useRouter();

  const { data: activeWatchlist, isError: isActiveWatchlistError } =
    useActiveWatchlist();
  const { mutate: updateActiveWatchlist } = useUpdateActiveWatchlist();
  const symbols = activeWatchlist?.symbols ?? [];

  const { data: symbolsScore = {}, isError: isSymbolScoreError } =
    useSymbolsScore({ symbolList: symbols });

  const [activeId, setActiveId] = useState<UniqueIdentifier | null>(null);

  const orientedValue = indexOrientedMap[orientedIndex];
  const { setIsOpen } = useWatchlistContext();

  const handleRemoveSymbol = (symbol: string) => {
    if (symbols.length === 0) return;
    updateActiveWatchlist(
      { symbols: symbols.filter((s) => s !== symbol) },
      {
        onSuccess: () => {
          toast.success(t.layout.watchlist.removeSymbolSuccessToast(symbol));
        },
        onError: () => {
          toast.error(t.error.serverError);
        },
      }
    );
  };

  const onSymbolClick = (symbol: string) => {
    if (window.innerWidth < 640) setIsOpen(false);

    if (/\/analysis\//.test(pathname)) {
      const newPathname = pathname.replace(
        /\/analysis\/[^/]+/,
        `/analysis/${symbol}`
      );
      router.push(newPathname);
    } else {
      router.push(`/${lang}/my/analysis/${symbol}`);
    }
  };

  const handleDeleteIconClick = (symbol: string) => {
    handleRemoveSymbol(symbol);
  };

  const handleDragStart = (event: DragEndEvent) => {
    setActiveId(event.active.id);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    const activeId = active?.id;
    const overId = over?.id;

    if (typeof activeId !== "string" || typeof overId !== "string") return;
    if (symbols.length === 0) return;

    const activeIndex = symbols.indexOf(activeId);
    const overIndex = symbols.indexOf(overId);
    const arrangedSymbols = arrayMove(symbols, activeIndex, overIndex);
    updateActiveWatchlist(
      { symbols: arrangedSymbols },
      {
        onError: () => {
          toast.error(t.error.serverError);
        },
      }
    );
    setActiveId(null);
  };

  const dndSensors = useSensors(
    useSensor(MouseSensor, {
      activationConstraint: {
        distance: 10,
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: isMobile ? 200 : 0,
        tolerance: 5,
      },
    })
  );

  if (symbols.length === 0) return <EmptyView />;
  if (isActiveWatchlistError || isSymbolScoreError)
    return <ErrorView>{t.error.serverError}</ErrorView>;

  return (
    <DndContext
      id="0"
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      sensors={dndSensors}
      modifiers={[restrictToVerticalAxis, restrictToParentElement]}
    >
      <div className="divide-y divide-gray-100 overflow-y-auto overscroll-y-none pb-2 text-center scrollbar-hide">
        <SortableContext
          items={activeWatchlist?.symbols ?? []}
          strategy={verticalListSortingStrategy}
        >
          {activeWatchlist?.symbols?.map((symbol) => {
            const symbolScore: number =
              symbolsScore?.[symbol]?.[orientedValue] ?? 0;
            const companyName = symbolsScore?.[symbol]?.companyName ?? "";
            return (
              <SymbolItem
                key={symbol}
                symbol={symbol}
                companyName={companyName}
                score={symbolScore}
                activeId={activeId}
                onSymbolClick={onSymbolClick}
                onRemoveClick={handleDeleteIconClick}
              />
            );
          })}
        </SortableContext>
      </div>
    </DndContext>
  );
};

const EmptyView = () => {
  const { t } = useLang();
  return (
    <div className="mt-4 flex flex-col items-center gap-4 px-2 py-8 text-center text-sm text-neutral-500">
      <Image src={emptyImage} alt="empty" />
      <p aria-label="watchlist is empty">
        <Balancer>{t.layout.watchlist.empty}</Balancer>
      </p>
    </div>
  );
};
